import { SegmentDto } from '../dtos/segment'
import { HIDDEN_IN_CREATION_FLOWS, SHOW_FIRST_IN_DROPDOWN } from '../constants'
import { ClockCircleOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons'
import React from 'react'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import { OptionGroupData } from '@pushly/aqe/lib/components/combo-segment-selection/combo-segment-selection'

type ReducedSegmentOptions = {
    displaySegments: {
        available: {
            recentlyUsed: SegmentDto[]
            all: SegmentDto[]
        }
    }
    systemSegments: SegmentDto[]
}

const buildOption = (segment: SegmentDto) => ({
    value: segment.id,
    label: segment.name,
    title: segment.name,
})

export const getAudienceSegmentOptionGroups = (
    segments: SegmentDto[],
    nestedKey: 'children' | 'options' = 'children',
): any[] => {
    if (!segments.length) {
        return []
    }

    const latestSegmentsUsed = [...segments]
        .sort((a, b) => {
            const aDate = a.lastTargetedDateUtc ? new Date(a.lastTargetedDateUtc).getTime() : 0
            const bDate = b.lastTargetedDateUtc ? new Date(b.lastTargetedDateUtc).getTime() : 0
            return bDate - aDate
        })
        .slice(0, 5)
        .map((s) => s.id)

    const { displaySegments, systemSegments } = segments.reduce<ReducedSegmentOptions>(
        (acc, next) => {
            if (
                next.type !== SegmentType.STANDARD &&
                !next.flags.includes(HIDDEN_IN_CREATION_FLOWS) &&
                next.flags.includes(SHOW_FIRST_IN_DROPDOWN)
            ) {
                acc.systemSegments.push(next)
            } else {
                if (latestSegmentsUsed.includes(next.id)) {
                    acc.displaySegments.available.recentlyUsed.push(next)
                } else {
                    acc.displaySegments.available.all.push(next)
                }
            }

            return acc
        },
        {
            displaySegments: {
                available: {
                    recentlyUsed: [],
                    all: [],
                },
            },
            systemSegments: [],
        } as ReducedSegmentOptions,
    )

    // don't alpha sort recently used, should order by date
    const recentlyUsedOptions = displaySegments.available.recentlyUsed.map(buildOption)
    const systemOptions = [...systemSegments].sort((a, b) => (a.name < b.name ? -1 : 1)).map(buildOption)
    const domainOptions = [...displaySegments.available.all].sort((a, b) => (a.name < b.name ? -1 : 1)).map(buildOption)

    return [
        {
            label: (
                <span>
                    <ClockCircleOutlined /> RECENTLY TARGETED
                </span>
            ),
            key: 1,
            [nestedKey]: recentlyUsedOptions,
        },
        {
            label: (
                <span>
                    <SettingOutlined /> SYSTEM
                </span>
            ),
            key: 2,
            [nestedKey]: systemOptions,
        },
        {
            label: (
                <span>
                    <TeamOutlined /> ALL SEGMENTS
                </span>
            ),
            key: 3,
            [nestedKey]: domainOptions,
        },
    ] as any[]
}
